import type { MenuDTOTypes } from '@repo/experience-api-client';
import { useMemo, useState } from 'react';

export const ALL_RECIPES_COLLECTION_SLUG = 'all-recipes';

type DietaryCollectionWithRecipeIds = {
  slug: string;
  name: string;
  recipeIds: string[];
  recipeCount: number;
};

type MenuCollectionWithDietaryCollections = {
  name: string;
  slug: string;
  recipeCount: number;
  dietaryCollectionsWithRecipeIds: DietaryCollectionWithRecipeIds[];
};

export function useMenuFilters(
  recipes: MenuDTOTypes.MenuRecipe[],
  collections: MenuCollectionWithDietaryCollections[],
  dietaryCollections: DietaryCollectionWithRecipeIds[] = [],
) {
  const ALL_RECIPES_COLLECTION: MenuCollectionWithDietaryCollections = useMemo(
    () => ({
      name: 'All Recipes',
      slug: ALL_RECIPES_COLLECTION_SLUG,
      recipeCount: recipes.length,
      dietaryCollectionsWithRecipeIds: dietaryCollections,
    }),
    [recipes, dietaryCollections],
  );

  const defaultDietaryCollectionsSlugs = new Map<string, boolean>();

  dietaryCollections.forEach((dc) => {
    defaultDietaryCollectionsSlugs.set(dc.slug, true);
  });

  const [activeCollectionSlug, setActiveCollectionSlug] = useState(ALL_RECIPES_COLLECTION.slug);
  const [activeDietaryCollectionsSlugs, setActiveDietaryCollectionsSlugs] = useState(
    defaultDietaryCollectionsSlugs,
  );

  const sortedCollections = useMemo(() => {
    return [ALL_RECIPES_COLLECTION, ...collections];
  }, [ALL_RECIPES_COLLECTION, collections]);

  const activeCollection = useMemo(() => {
    return sortedCollections.find((collection) => collection.slug === activeCollectionSlug);
  }, [sortedCollections, activeCollectionSlug]);

  const activeDietaryCollectionsWithCounts = useMemo(() => {
    if (!activeCollection) return dietaryCollections;

    return activeCollection.dietaryCollectionsWithRecipeIds;
  }, [activeCollection, dietaryCollections]);

  const filteredRecipes = useMemo(() => {
    if (!activeCollection) return recipes;

    const recipesInCollection =
      activeCollectionSlug === ALL_RECIPES_COLLECTION_SLUG
        ? recipes
        : recipes.filter(({ collections }) =>
            collections.some((collection) => collection.slug === activeCollectionSlug),
          );

    const allDietaryFiltersActive = Array.from(activeDietaryCollectionsSlugs.values()).every(
      (isActive) => isActive,
    );

    const allowedRecipeIdsFromActiveDietaryCollections = new Set<string | number>(
      activeCollection.dietaryCollectionsWithRecipeIds
        .filter(({ slug }) => activeDietaryCollectionsSlugs.get(slug))
        .flatMap(({ recipeIds }) => recipeIds),
    );

    return recipesInCollection.filter(({ id }) => {
      if (allDietaryFiltersActive) {
        return true;
      }

      return allowedRecipeIdsFromActiveDietaryCollections.has(id);
    });
  }, [activeCollection, recipes, activeCollectionSlug, activeDietaryCollectionsSlugs]);

  const onChooseCollection = (slug: string) => {
    setActiveCollectionSlug(slug);
  };

  const onDietaryToggle = (slug: string) => {
    setActiveDietaryCollectionsSlugs((current) => {
      if (!current.has(slug)) {
        return current;
      }

      const newMap = new Map(current);
      newMap.set(slug, !current.get(slug));
      return newMap;
    });
  };

  const activeCollectionName = useMemo(() => {
    return activeCollectionSlug === ALL_RECIPES_COLLECTION_SLUG
      ? ALL_RECIPES_COLLECTION.name
      : collections.find((collection) => collection.slug === activeCollectionSlug)!.name;
  }, [ALL_RECIPES_COLLECTION, collections, activeCollectionSlug]);

  const onResetDietaryFilters = () => {
    setActiveDietaryCollectionsSlugs(defaultDietaryCollectionsSlugs);
  };

  return {
    activeCollectionSlug,
    activeDietaryCollectionsSlugs,
    activeDietaryCollectionsWithCounts,
    sortedCollections,
    onChooseCollection,
    filteredRecipes,
    activeCollectionName,
    onDietaryToggle,
    onResetDietaryFilters,
  };
}
